import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook to manage student view registration with duplicate prevention.
 * Tracks registered views using a Set and clears when navigation changes.
 *
 * @param clearDeps - Dependencies that trigger clearing of registered views when they change
 * @returns Object with `registerViewIfNeeded` function to register a view (only if not already registered)
 */
export function useStudentViewRegistration<T extends unknown[]>(
  clearDeps: T,
): {
  registerViewIfNeeded: (viewKey: string, onRegister: () => void) => void;
} {
  // Track registered student views using a Set to prevent race conditions
  // Key format: `${studentId}#${intakeId}#${offerId}`
  const registeredViews = useRef<Set<string>>(new Set());
  const previousClearKey = useRef<string | null>(null);

  // Clear registered views when navigation dependencies change
  useEffect(() => {
    // Create a key from the dependencies to detect changes
    const currentClearKey = JSON.stringify(clearDeps);

    if (previousClearKey.current !== null && previousClearKey.current !== currentClearKey) {
      registeredViews.current.clear();
    }

    previousClearKey.current = currentClearKey;
  }, clearDeps);

  /**
   * Register a view only if it hasn't been registered yet.
   * @param viewKey - Unique key for the view (format: `${studentId}#${intakeId}#${offerId}`)
   * @param onRegister - Callback to execute when registering the view
   */
  const registerViewIfNeeded = useCallback((viewKey: string, onRegister: () => void): void => {
    if (!registeredViews.current.has(viewKey)) {
      registeredViews.current.add(viewKey);
      onRegister();
    }
  }, []);

  return { registerViewIfNeeded };
}
